import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useAuth, homeFor } from "../context/AuthContext";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/tax-rescue", label: "Tax Rescue" },
  { to: "/resources", label: "AZ Resources" },
];

const LEGAL = [
  ["terms", "Terms of Service"], ["privacy", "Privacy Policy"],
  ["e-communications", "Electronic Communications Consent"],
  ["payment-authorization", "Payment Authorization"],
  ["filing-authorization", "Tax Filing Authorization"],
  ["security", "Security Policy"], ["refund", "Refund & Cancellation"],
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="seal-strip" />
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/logo.png" alt="AZ Tax Accounts" className="h-10 w-10 rounded-full border border-slate-200 object-cover" />
            <div className="leading-tight">
              <div className="font-extrabold tracking-tight text-navy">AZ <span className="text-copper">TAX</span> ACCOUNTS</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Business Tax Management Made Simple</div>
            </div>
          </Link>
          <nav className="hidden items-center gap-6 md:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => `text-sm font-semibold ${isActive ? "text-copper" : "text-slate-600 hover:text-navy"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            {user ? (
              <Link to={homeFor(user)} className="btn-navy" data-testid="nav-dashboard-btn">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="btn-outline-gov" data-testid="nav-login-btn">Login</Link>
                <Link to="/register" className="btn-copper" data-testid="nav-get-started-btn">Get Started</Link>
              </>
            )}
          </div>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X className="h-6 w-6 text-navy" /> : <Menu className="h-6 w-6 text-navy" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-slate-200 bg-white px-4 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-semibold text-slate-700">{n.label}</Link>
              ))}
              <div className="flex gap-2 pt-2">
                <Link to="/login" className="btn-outline-gov flex-1">Login</Link>
                <Link to="/register" className="btn-copper flex-1">Get Started</Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main><Outlet /></main>

      <footer className="border-t-4 border-navy bg-white text-slate-600">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="" className="h-9 w-9 rounded-full" />
              <span className="font-extrabold text-navy">AZ <span className="text-copper">TAX</span> ACCOUNTS</span>
            </div>
            <p className="mt-3 text-sm text-slate-500">Connect. Calculate. Reserve. File. Pay. Prove.</p>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-copper" /> Secure by design. Audit-logged. Role-based access.</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-copper">Platform</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/services" className="hover:text-navy">Services</Link></li>
              <li><Link to="/pricing" className="hover:text-navy">Pricing</Link></li>
              <li><Link to="/tax-rescue" className="hover:text-navy">AZ Tax Rescue</Link></li>
              <li><Link to="/resources" className="hover:text-navy">AZ Tax Relief Resources</Link></li>
              <li><Link to="/register" className="hover:text-navy">Open an Account</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-copper">Legal</div>
            <ul className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              {LEGAL.map(([slug, label]) => (
                <li key={slug}><Link to={`/legal/${slug}`} className="hover:text-navy" data-testid={`footer-legal-${slug}`}>{label}</Link></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} AZ Tax Accounts. Estimates are not filed tax returns or professional tax determinations.
        </div>
      </footer>
    </div>
  );
}
